// src/components/FormSettings/FormDesignLayout/FieldSettingsUI/FileBasicSettings.jsx
// -----------------------------------------------------------------------------
// FileBasicSettings – consolidated "Basic" panel for the 'attach' field type.
// Purpose (UI-only):
//   • Let a librarian configure the file attachment UI (not metadata fields!)
//   • Minimal knobs to switch Compact/Preview, allow multiple, accept list,
//     max size, and a couple of preview UI defaults.
//
// IMPORTANT: Asset metadata like name/description/tags should be added as
// regular form fields elsewhere in the builder (text, multi-select, etc.).
//
// Wiring notes:
//   • Reads selectedFieldForEdit from FieldEditContext
//   • Debounced update via updateField(id, patch) – your context merges patches
//   • No backend calls here; all changes are persisted in fieldsMetadata
// -----------------------------------------------------------------------------

import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  FormControlLabel,
  Switch,
  Divider,
  Button,
} from '@mui/material';
import debounce from 'lodash/debounce';
import { useFieldEdit } from '../../../../contexts/FieldEditContext';
import SmartButtonOutlinedIcon from '@mui/icons-material/SmartButtonOutlined';
import CollectionsOutlinedIcon from '@mui/icons-material/CollectionsOutlined';

const FileBasicSettings = () => {
  const { selectedFieldForEdit, updateField } = useFieldEdit();

  // Tiny trace of the current selection for validation while integrating
  console.log('[FileBasicSettings] selectedFieldForEdit:', selectedFieldForEdit);

  // Local mirror of attach field metadata with safe defaults
  const [localValues, setLocalValues] = useState({
    // Basics
    label: '',
    helperText: '',
    required: false,
    hidden: false,

    // Attach-specific behavior
    displayMode: 'compact',   // 'compact' | 'preview'
    allowMultiple: false,

    // File rules
    accept: ['image/*'],      // stored as string[]; edited via CSV text input
    maxSizeMB: 50,

    // Preview-mode UI (minimal tweaks for now)
    ui: {
      dropzoneHeight: '180px',
      badgePosition: 'top-right',

      // NEW: Compact button visual sizing (AttachFieldComponent reads these)
      compactButtonHeight: 32, // px
     compactButtonWidth: 140, // px (numeric default, aligns with component)
    },
  });

  // Represent accept[] as CSV in the text input
  const acceptString = Array.isArray(localValues.accept)
    ? localValues.accept.join(', ')
    : '';

  // ────────────────────────────────────────────────────────────────────────────
  // Sync local state from selected field metadata
  // ────────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!selectedFieldForEdit?.metadata) return;
    const m = selectedFieldForEdit.metadata;

    // Only handle the attach field here
    if (m.type !== 'attach') return;

    console.log('[FileBasicSettings] syncing from metadata:', m);

    setLocalValues({
      label: m.label || '',
      helperText: m.helperText || '',
      required: !!m.required,
      hidden: !!m.hidden,

      displayMode: m.displayMode || 'compact',
      allowMultiple: !!m.allowMultiple,

      accept: Array.isArray(m.accept) && m.accept.length ? m.accept : ['image/*'],
      maxSizeMB: typeof m.maxSizeMB === 'number' ? m.maxSizeMB : 50,

      ui: {
        dropzoneHeight: m.ui?.dropzoneHeight || '180px',
        badgePosition : m.ui?.badgePosition  || 'top-right',

        // NEW: inherit existing or default compact button sizing
        compactButtonHeight:
          Number.isFinite(m.ui?.compactButtonHeight) ? m.ui.compactButtonHeight : 32,
     compactButtonWidth:
       Number.isFinite(m.ui?.compactButtonWidth) ? m.ui.compactButtonWidth : 140, // numeric px

      },
    });
  }, [selectedFieldForEdit]);

  // ────────────────────────────────────────────────────────────────────────────
  // Debounced update into FieldEditContext → FieldMetadataContext
  // (Your context merges the patch with existing metadata)
  // ────────────────────────────────────────────────────────────────────────────
  const debouncedPush = useCallback(
    debounce((id, patch) => {
      updateField(id, patch);
      console.log('[FileBasicSettings] updateField →', id, patch);
    }, 260),
    [updateField]
  );

  // Cleanup to avoid a late patch after unmount / field switch
  useEffect(() => () => debouncedPush.cancel(), [debouncedPush]);

  const patchMeta = (partial) => {
    if (!selectedFieldForEdit?.id) return;
    debouncedPush(selectedFieldForEdit.id, partial);
  };

  // Generic key/value setter for top-level metadata
  const handleChange = (key, value) => {
    console.log('[FileBasicSettings] handleChange', { key, value });
    setLocalValues((prev) => {
      const next = { ...prev, [key]: value };
      patchMeta({ [key]: value });
      return next;
    });
  };

  // Toggle helpers (required / hidden)
  const handleToggle = (key) => (e) => {
    const checked = !!e.target.checked;
    console.log('[FileBasicSettings] toggle', { key, checked });
    setLocalValues((prev) => {
      const next = { ...prev, [key]: checked };
      patchMeta({ [key]: checked });
      return next;
    });
  };

  // Comma-separated list → string[]
  const handleAcceptChange = (raw) => {
    const arr = raw
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    console.log('[FileBasicSettings] accept change', { raw, arr });
    setLocalValues((prev) => {
      const next = { ...prev, accept: arr };
      patchMeta({ accept: arr });
      return next;
    });
  };

  // Max size (MB) → numeric
  const handleMaxSizeChange = (raw) => {
    const n = Number(raw);
    const safe = Number.isFinite(n) && n >= 0 ? n : 0;
    console.log('[FileBasicSettings] maxSizeMB change', { raw, safe });
    setLocalValues((prev) => {
      const next = { ...prev, maxSizeMB: safe };
      patchMeta({ maxSizeMB: safe });
      return next;
    });
  };

  // Nested UI changes
  const handleUIChange = (key, value) => {
    console.log('[FileBasicSettings] ui change', { key, value });
    setLocalValues((prev) => {
      const ui = { ...prev.ui, [key]: value };
      patchMeta({ ui });
      return { ...prev, ui };
    });
  };

  // NEW: numeric coercion for compactButtonHeight (px)
  const handleCompactButtonHeightChange = (raw) => {
    if (raw === '') {
      console.log('[FileBasicSettings] compactButtonHeight → reset to default (32)');
      handleUIChange('compactButtonHeight', 32);
      return;
    }
    const n = Number(raw);
    const safe = Number.isFinite(n) && n > 0 ? n : 32;
    console.log('[FileBasicSettings] compactButtonHeight change', { raw, safe });
    handleUIChange('compactButtonHeight', safe);
  };

 // NEW: numeric coercion for compactButtonWidth (px)
 const handleCompactButtonWidthChange = (raw) => {
   if (raw === '') {
     console.log('[FileBasicSettings] compactButtonWidth → reset to default (140)');
     handleUIChange('compactButtonWidth', 140);
     return;
   }
   const n = Number(raw);
   const safe = Number.isFinite(n) && n > 0 ? n : 140;
   console.log('[FileBasicSettings] compactButtonWidth(px) change', { raw, safe });
   handleUIChange('compactButtonWidth', safe);
 };



  // ────────────────────────────────────────────────────────────────────────────
  // Guards – keep UX consistent with your other panels
  // ────────────────────────────────────────────────────────────────────────────
  if (!selectedFieldForEdit?.metadata) {
    return <Typography>Select a field to edit</Typography>;
  }
  if (selectedFieldForEdit.metadata.type !== 'attach') {
    return (
      <Typography variant="body2" color="text.secondary">
        FileBasicSettings applies to <strong>Attach</strong> fields. Selected type is{' '}
        <strong>{selectedFieldForEdit.metadata.type || '(unknown)'}</strong>.
      </Typography>
    );
  }

  // ────────────────────────────────────────────────────────────────────────────
  // Render – minimal, scalable, consistent with your design language
  // ────────────────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ maxWidth: '90%', mx: 'auto', mt: 2 }}>
      {/* Header */}
      <Typography variant="body2" sx={{ ml: 1, mb: 1.5 }}>
        <strong>Field Type:</strong> {selectedFieldForEdit.metadata.type}
      </Typography>

      {/* Make required */}
      <FormControlLabel
        control={
          <Switch
            checked={!!localValues.required}
            onChange={(e) => handleChange('required', e.target.checked)}
            name="requiredToggle"
            color="primary"
            size="small"
          />
        }
        label={
          <Typography variant="body2" sx={{ fontWeight: 'light', color: 'text.secondary' }}>
            Make required
          </Typography>
        }
        sx={{ ml: 1, mb: 1 }}   // ← small gap before the next stacked toggle
      />

     {/* Allow multiple files (stacked like BasicSettings) */}
     <FormControlLabel
       control={
         <Switch
           checked={!!localValues.allowMultiple}
           onChange={(e) => handleChange('allowMultiple', e.target.checked)}
           name="allowMultipleToggle"
           color="primary"
           size="small"
         />
       }
       label={
         <Typography variant="body2" sx={{ fontWeight: 'light', color: 'text.secondary' }}>
           Allow multiple
         </Typography>
       }
       sx={{ ml: 1, mb: 1 }}   // ← same spacing rhythm as BasicSettings
     />


      {/* Compact vs Preview – icon-only buttons with caption INSIDE the border */}
      <Box
        sx={{
          display: 'flex',
          gap: 1.5,
          justifyContent: 'flex-start',
          mt: 0.5,
          mb: 4,
          width: '100%',
       }}
     >
        {[
          {
            key: 'compact',
            Icon: SmartButtonOutlinedIcon, // outline button metaphor
            caption: 'compact',
          },
          {
            key: 'preview',
            Icon: CollectionsOutlinedIcon, // gallery/preview metaphor
            caption: 'Preview',
          },
        ].map((opt) => (
          <Button
            key={opt.key}
            variant={localValues.displayMode === opt.key ? 'contained' : 'outlined'}
            onClick={() => handleChange('displayMode', opt.key)}
            sx={{
              flex: '1 1 0',
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              py: 1.25,
              textTransform: 'none',
              gap: 0.5, // space between icon and caption
            }}
            aria-pressed={localValues.displayMode === opt.key ? 'true' : 'false'}
          >
            <opt.Icon sx={{ fontSize: 24 }} />
            <Typography
              variant="caption"
              color="inherit" // inherit so contained/outlined contrast stays readable
              sx={{ lineHeight: 1.2 }}
            >
              {opt.caption}
            </Typography>
          </Button>
        ))}
      </Box>

      {/* Label / Helper (stacked, no box — like BasicSettings) */}
      <TextField
        fullWidth
        label="Label"
        value={localValues.label}
        onChange={(e) => handleChange('label', e.target.value)}
        sx={{ mb: 2 }}
      />
      <TextField
        fullWidth
        label="Helper text"
        value={localValues.helperText}
        onChange={(e) => handleChange('helperText', e.target.value)}
        sx={{ mb: 2 }}
      />



      {/* Display Mode + Allow Multiple + (NEW) Compact Button Sizing */}
      <Box
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          p: 2,
          mb: 2,
          position: 'relative',
        }}
      >
        <Typography
          variant="caption"
          sx={{
            position: 'absolute',
            top: '-10px',
            left: '10px',
            backgroundColor: 'background.paper',
            px: 1,
            fontWeight: 'bold',
            color: 'text.secondary',
          }}
        >
          Display Settings
        </Typography>

       {/* Preview surface height (only when Preview mode is selected) */}
       {localValues.displayMode === 'preview' && (
         <Box sx={{ mt: 2 }}>
           <TextField
             fullWidth
             label="Drop-zone height (CSS)"
             placeholder="e.g. 180px"
             value={localValues.ui.dropzoneHeight}
             onChange={(e) => handleUIChange('dropzoneHeight', e.target.value)}
             helperText="Controls Preview mode surface height (e.g., 180px, 12rem)."
           />
         </Box>
       )}


        {/* NEW: Compact Button sizing controls */}
        <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
          <TextField
            type="number"
            label="Button height (px)"
            inputProps={{ min: 1 }}
            value={
              Number.isFinite(localValues.ui.compactButtonHeight)
                ? localValues.ui.compactButtonHeight
                : 32
            }
            onChange={(e) => handleCompactButtonHeightChange(e.target.value)}
           helperText={
             localValues.displayMode === 'compact'
               ? 'Compact button'
               : 'Preview button'
           }

            sx={{ width: 220 }}
          />

         <TextField
           type="number"
           label="Button width (px)"
           inputProps={{ min: 1 }}
           value={
             Number.isFinite(localValues.ui.compactButtonWidth)
               ? localValues.ui.compactButtonWidth
               : 140
           }
           onChange={(e) => handleCompactButtonWidthChange(e.target.value)}
           helperText={
             localValues.displayMode === 'compact'
               ? 'Compact button'
               : 'Preview button'
           }
           sx={{ width: 220 }}
         />

        </Box>
      </Box>

    </Box>
  );
};

export default FileBasicSettings;
